/**
 * Zippie celebratory sound — plays a short chirpy chime followed by the
 * spoken word "Zippie!" when a child answers correctly.
 *
 * Uses the Web Audio API (chime) + SpeechSynthesis (spoken word). Zero
 * dependencies, works offline, and gracefully no-ops in environments where
 * either API is unavailable (older browsers, muted contexts, etc.).
 */

let audioCtx = null;

const ensureAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioCtx || audioCtx.state === 'closed') {
    try { audioCtx = new AudioContextClass(); } catch { return null; }
  }
  if (audioCtx.state === 'suspended') { audioCtx.resume().catch(() => {}); }
  return audioCtx;
};

const playChime = () => {
  const ctx = ensureAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  // Two short ascending notes — classic "success" chirp
  const notes = [
    { freq: 660, start: 0.00, dur: 0.14 },   // E5
    { freq: 990, start: 0.14, dur: 0.20 },   // B5
  ];

  notes.forEach(({ freq, start, dur }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + start);
    gain.gain.setValueAtTime(0.0001, now + start);
    gain.gain.exponentialRampToValueAtTime(0.25, now + start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.02);
  });
};

const speakZippie = () => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    // Cancel any queued utterances so we never stack
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance('Zippie!');
    utter.rate = 1.05;
    utter.pitch = 1.4;
    utter.volume = 1.0;
    // Prefer a warmer voice if available
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /Samantha|Karen|Google US English|Google UK English Female/i.test(v.name));
    if (preferred) utter.voice = preferred;
    window.speechSynthesis.speak(utter);
  } catch {
    // no-op
  }
};

/** Play the celebratory chime + "Zippie!" voice. Safe to call from any UI event. */
export const playZippieSound = () => {
  playChime();
  // Slight delay so the spoken word lands right after the chime
  setTimeout(speakZippie, 320);
};

export default playZippieSound;
